import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import * as actionTypes from "../actions/types.js";
import * as feedActionTypes from "./actionTypes.ts";
import { feedService } from "../../services/feedService.ts";
import type { FeedPost, FeedFilterOption, FeedPortfolio } from "../../types/feed.ts";
import type { NavigateFunction } from "react-router-dom";
import { openEdifySpinner, closeMainModal } from "../actions/profileActions.js";
import { openMainModalDashboard } from "../actions/dashboardActions.js";
import { setFeedItemOrder } from "./actions.ts";
import { computeItemOrder } from "./selectors.ts";
import { fireAnalytics } from "../../utils/firebase.js";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";

type FeedState = {
  feedView?: { selectedOption: string };
  posteos?: FeedPost[];
  ownPosteos?: FeedPost[];
  allPortfolios?: FeedPortfolio[];
  userPortfolios?: FeedPortfolio[];
};
type GeneralState = { userData?: { id?: number | string } };
type RootState = { feed?: FeedState; general?: GeneralState };

type AppDispatch = ThunkDispatch<RootState, void, AnyAction>;

type PostWithId = { id: number; [key: string]: unknown };
type CommentsResult = { id: number; comments: FeedPost["comments"] };

const fetchCommentsForPosts = (posts: PostWithId[]): Promise<CommentsResult[]> => {
  if (!Array.isArray(posts) || posts.length === 0) return Promise.resolve([]);
  return Promise.all(
    posts.map((post) =>
      feedService
        .getPostComments(post.id)
        .then((comments) => ({ id: post.id, comments }))
        .catch(() => ({ id: post.id, comments: [] }))
    )
  );
};

type FetchFeedOptions = { silent?: boolean };

export const fetchPosteosWithComments =
  (order: string, type: string, options?: FetchFeedOptions) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void | unknown> => {
    const silent = options?.silent === true;
    if (!silent) dispatch({ type: actionTypes.START_LOADING });
    const endLoading = () => {
      if (!silent) dispatch({ type: actionTypes.END_LOADING });
    };
    return feedService
      .getPosteos(order, type)
      .then((posts) => {
        const postsArray = Array.isArray(posts) ? posts : [];
        const postsWithEmptyComments = (postsArray as FeedPost[]).map((p) => ({
          ...p,
          comments: p.comments ?? [],
        }));
        dispatch({ type: actionTypes.GET_POSTEOS, payload: postsWithEmptyComments });
        const state = getState();
        const orderPayload = computeItemOrder(
          postsWithEmptyComments,
          state.feed?.ownPosteos ?? [],
          (state.feed?.allPortfolios ?? []) as FeedPortfolio[],
          (state.feed?.feedView?.selectedOption ?? "Mostrar") as FeedFilterOption,
          state.general?.userData?.id
        );
        dispatch(setFeedItemOrder(orderPayload));
        endLoading();
        fetchCommentsForPosts(postsArray as unknown as PostWithId[])
          .then((commentsList) => {
            const byId = (commentsList || []).reduce<Record<number, FeedPost["comments"]>>(
              (acc, { id, comments }) => {
                acc[id] = comments;
                return acc;
              },
              {}
            );
            dispatch({
              type: feedActionTypes.FEED_MERGE_POSTS_COMMENTS,
              payload: { commentsByPostId: byId },
            });
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
        endLoading();
      });
  };

export const fetchUserPortfolios =
  (userIdParam: number | string) =>
  (dispatch: AppDispatch): Promise<void | unknown> => {
    return feedService
      .getUserPortfolios(userIdParam)
      .then((portfolios) => {
        const portfoliosArray = Array.isArray(portfolios) ? portfolios : [];
        dispatch({
          type: actionTypes.GET_USER_PORTFOLIOS,
          payload: portfoliosArray,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

export const fetchOwnFeedContent =
  (userIdParam: number | string, options?: FetchFeedOptions) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void | unknown> => {
    const silent = options?.silent === true;
    if (!silent) dispatch({ type: actionTypes.START_LOADING });
    const endLoading = () => {
      if (!silent) dispatch({ type: actionTypes.END_LOADING });
    };
    return Promise.all([
      feedService.getOwnPosteos(userIdParam),
      feedService.getUserPortfolios(userIdParam),
    ])
      .then(([posts, portfolios]) => {
        const postsArray = Array.isArray(posts) ? posts : [];
        const portfoliosArray = Array.isArray(portfolios) ? portfolios : [];
        const postsWithEmptyComments = (postsArray as FeedPost[]).map((p) => ({
          ...p,
          comments: p.comments ?? [],
        }));
        dispatch({
          type: actionTypes.GET_OWN_POSTEOS,
          payload: postsWithEmptyComments,
        });
        dispatch({
          type: actionTypes.GET_USER_PORTFOLIOS,
          payload: portfoliosArray,
        });
        const state = getState();
        const orderPayload = computeItemOrder(
          state.feed?.posteos ?? [],
          postsWithEmptyComments,
          (state.feed?.allPortfolios ?? []) as FeedPortfolio[],
          "propios",
          state.general?.userData?.id,
          portfoliosArray as FeedPortfolio[]
        );
        dispatch(setFeedItemOrder(orderPayload));
        endLoading();
        fetchCommentsForPosts(postsArray as unknown as PostWithId[])
          .then((commentsList) => {
            const byId = (commentsList || []).reduce<Record<number, FeedPost["comments"]>>(
              (acc, { id, comments }) => {
                acc[id] = comments;
                return acc;
              },
              {}
            );
            dispatch({
              type: feedActionTypes.FEED_MERGE_POSTS_COMMENTS,
              payload: { commentsByPostId: byId },
            });
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
        endLoading();
      });
  };

export const refetchFeedCurrentView =
  (silent?: boolean) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const feedView = getState().feed?.feedView;
    const userId = getState().general?.userData?.id;
    if (!feedView) return;
    const opts = silent ? { silent: true } : undefined;
    switch (feedView.selectedOption) {
      case "recientes":
      case "Mostrar":
        dispatch(fetchPosteosWithComments("DESC", "DATE", opts));
        break;
      case "recomendados":
        dispatch(fetchPosteosWithComments("ASC", "MG", opts));
        break;
      case "propios":
        if (userId != null) dispatch(fetchOwnFeedContent(userId, opts));
        break;
      case "productos":
      case "servicios":
      case "posteos":
        break;
      default:
        dispatch(fetchPosteosWithComments("DESC", "DATE", opts));
    }
  };

export const getPosteoById =
  (id: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { post, comments } = await feedService.getPostWithComments(id);
      dispatch({
        type: actionTypes.GET_POSTEO_WITH_COMMENTS,
        payload: { post, comments },
      });
    } catch (err) {
      console.log(err);
    }
  };

type CreatePostBody = { images?: unknown; video?: string; content?: string };

export const createProfilePost =
  (body: CreatePostBody) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<unknown> => {
    const { images, video, content } = body;
    return feedService
      .createPost({ content, video, images })
      .then((data) => {
        dispatch({ type: actionTypes.RECENT_POST, payload: data });
        dispatch({ type: feedActionTypes.FEED_PREPEND_POST, payload: data });
        dispatch(refetchFeedCurrentView(true));
        const userData = getState().general?.userData;
        fireAnalytics("post_created", {
          edify_user_id: userData?.id ?? "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "publishing",
          action: "post_created",
          post_id: data?.id ?? "",
          post_type: "profile_post",
          has_images: !!images,
          has_video: !!video,
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };

export const createPost =
  (body: CreatePostBody, navigate?: NavigateFunction) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { images, video, content } = body;
    dispatch(openEdifySpinner(true));
    feedService
      .createPost({ content, video, images })
      .then((data) => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu posteo se publicó en Explorar posteos, servicios y productos.",
            iconType: "success",
            isSuccess: true,
            noLabel: "Ver en Explorar",
            noActionNeeded: false,
            customCloseMainModal: () => {
              dispatch(closeMainModal());
              navigate?.("/marketplace/explorar");
            },
          })
        );
        dispatch({ type: actionTypes.RECENT_POST, payload: data });
        dispatch({ type: feedActionTypes.FEED_PREPEND_POST, payload: data });
        dispatch(refetchFeedCurrentView(true));
        const userData = getState().general?.userData;
        fireAnalytics("post_created", {
          edify_user_id: userData?.id ?? "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "publishing",
          action: "post_created",
          post_id: data?.id ?? "",
          post_type: "feed_post",
          has_images: !!images,
          has_video: !!video,
        });
      })
      .catch(() => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            iconType: "error",
            body: "Tu posteo no se ha podido crear, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      });
  };

export const deletePost =
  (id: number) =>
  (dispatch: AppDispatch): void => {
    feedService
      .deletePost(id)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu posteo se ha eliminado con éxito",
            isSuccess: true,
            confirmHandler: null,
          })
        );
        dispatch(refetchFeedCurrentView());
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu posteo no se ha podido eliminar, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: false,
            confirmHandler: null,
          })
        );
      });
  };

export const likePost =
  (id: number | string) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const postIdNum = Number(id);
    const state = getState();
    const userId = state.general?.userData?.id;
    const post =
      (state.feed?.posteos ?? []).find((p) => p.id === postIdNum || String(p.id) === String(id)) ??
      (state.feed?.ownPosteos ?? []).find((p) => p.id === postIdNum || String(p.id) === String(id));
    const currentLikes = post?.likes ?? [];
    const previousLikes = [...currentLikes];
    const isLiked = previousLikes.some(
      (l) => l?.user?.id != null && String(l.user.id) === String(userId)
    );
    dispatch({
      type: feedActionTypes.FEED_OPTIMISTIC_LIKE_POST,
      payload: { postId: postIdNum, userId, add: !isLiked },
    });
    const add = !isLiked;
    return feedService
      .likePost(postIdNum)
      .then((res) => {
        const likes = res?.likes != null && Array.isArray(res.likes) ? res.likes : null;
        const userInLikes =
          likes != null &&
          userId != null &&
          likes.some((l) => l?.user?.id != null && String(l.user.id) === String(userId));
        const confirmed = add ? userInLikes : !userInLikes;
        const likesToApply = likes != null && confirmed ? likes : previousLikes;
        dispatch({
          type: feedActionTypes.FEED_MERGE_POST_PARTIAL,
          payload: { postId: postIdNum, likes: likesToApply },
        });
      })
      .catch(() => {
        dispatch({
          type: feedActionTypes.FEED_MERGE_POST_PARTIAL,
          payload: { postId: postIdNum, likes: previousLikes },
        });
      });
  };

type EditPosteoBody = {
  id: number | string;
  images?: string;
  video?: string;
  content?: string;
};

export const editPosteo =
  (body: EditPosteoBody) =>
  (dispatch: AppDispatch): void => {
    const { id, content, video, images } = body;
    dispatch(openEdifySpinner(true));
    feedService
      .updatePost({
        id: parseInt(String(id), 10),
        content,
        video,
        images,
      })
      .then((data) => {
        dispatch(openEdifySpinner(false));
        dispatch({ type: actionTypes.UPDATE_POSTEO_INFO, payload: { posteo: data } });
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu posteo se ha editado con éxito",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      })
      .catch(() => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu posteo no se ha podido editar, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const createPostComment =
  (postId: number, content: string) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .createPostComment(postId, content)
      .then(() => {
        dispatch(getPosteoById(postId));
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario ha sido publicado en el muro",
            iconType: "success",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu comentario no se ha podido publicar, por favor intenta nuevamente o escribenos a contact@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: false,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const editPostComment =
  (commentId: number, content: string, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .editPostComment(commentId, content)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario se ha editado con éxito",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            iconType: "error",
            body: "Tu comentario no se ha podido editar, por favor intenta nuevamente o escribenos a contacto@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      });
  };

export const likePostComment =
  (commentId: number, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .likePostComment(commentId)
      .then(() => dispatch(getPosteoById(postId)))
      .catch((err) => {
        console.log(err);
      });
  };

export const responsePostComment =
  (postId: number, commentId: number, content: string) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .responsePostComment(postId, commentId, content)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu respuesta ha sido publicada en el muro",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu respuesta no se ha podido publicar, por favor intenta nuevamente o escribenos a contacto@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const deletePostComment =
  (commentId: number, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .deletePostComment(commentId)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario se ha eliminado con éxito",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Error!",
            body: "Tu comentario no se ha podido eliminar",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };
